<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Modal from 'dashboard/components/Modal.vue';

const props = defineProps({
  onClose: {
    type: Function,
    default: () => {},
  },
});

const emit = defineEmits(['confirm']);

const store = useStore();
const { t } = useI18n();

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const currentUser = useMapGetter('getCurrentUser');

const show = ref(true);
const fileInputRef = ref(null);
const visibility = ref('public_response');
const selectedUserIds = ref([]);
const selectedTeamIds = ref([]);

const agentMultiSelectOptions = computed(() =>
  (agents.value || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  }))
);

const teamMultiSelectOptions = computed(() =>
  (teams.value || []).map(team => ({
    value: team.id,
    label: team.name,
  }))
);

const isPrivate = computed(() => visibility.value === 'private_response');

const isFormInvalid = computed(() => {
  if (!isPrivate.value) return false;
  return (
    selectedUserIds.value.length === 0 && selectedTeamIds.value.length === 0
  );
});

const isVisibilityActive = key =>
  visibility.value === key
    ? 'border-[rgba(74,222,128,0.5)] bg-[rgba(74,222,128,0.08)] shadow-[0_0_12px_rgba(74,222,128,0.15)]'
    : 'border-white/10 bg-white/5';

const formatStepNumber = step => String(step).padStart(2, '0');

const ensureCurrentUserSelected = () => {
  const userId = currentUser.value?.id;
  if (!userId) return;
  if (!selectedUserIds.value.includes(userId)) {
    selectedUserIds.value = [...selectedUserIds.value, userId];
  }
};

const onUpdateVisibility = value => {
  visibility.value = value;
  selectedUserIds.value = [];
  selectedTeamIds.value = [];
  if (value === 'private_response') {
    ensureCurrentUserSelected();
  }
};

const onSubmit = () => {
  if (isFormInvalid.value) return;
  fileInputRef.value?.click();
};

const onFileSelected = event => {
  const input = event.target;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;

  emit('confirm', {
    file,
    visibility: visibility.value,
    user_ids: isPrivate.value ? selectedUserIds.value : [],
    team_ids: isPrivate.value ? selectedTeamIds.value : [],
  });
};

onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});
</script>

<template>
  <Modal v-model:show="show" :on-close="props.onClose">
    <div class="flex flex-col gap-0">
      <div class="px-8 pt-8 pb-6 border-b border-white/10">
        <p
          class="text-xs font-semibold tracking-[0.2em] text-[#4ade80] uppercase mb-1"
        >
          {{ t('CANNED_MGMT.IMPORT.TITLE') }}
        </p>
        <h2 class="text-3xl font-black tracking-wide text-white uppercase">
          {{ t('CANNED_MGMT.IMPORT.DESC') }}
        </h2>
      </div>

      <form class="flex flex-col gap-6 px-8 py-6" @submit.prevent="onSubmit">
        <input
          ref="fileInputRef"
          type="file"
          accept=".xlsx,.tsv,.csv,.txt,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/csv,text/tab-separated-values"
          class="hidden"
          @change="onFileSelected"
        />

        <div class="flex flex-col gap-3">
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-[#4ade80] tracking-widest">{{
              formatStepNumber(1)
            }}</span>
            <span
              class="text-xs font-semibold tracking-[0.18em] text-n-slate-10 uppercase"
            >
              {{ t('CANNED_MGMT.ADD.FORM.VISIBILITY.LABEL') }}
            </span>
          </div>
          <div class="grid grid-cols-2 gap-3">
            <button
              type="button"
              class="p-3 relative rounded-xl border text-left transition-all duration-200"
              :class="isVisibilityActive('public_response')"
              @click="onUpdateVisibility('public_response')"
            >
              <span
                v-if="visibility === 'public_response'"
                class="i-ph-check-circle-fill absolute text-[#4ade80] top-3 right-3 size-4"
              />
              <p class="text-sm font-semibold text-white m-0">
                {{ t('CANNED_MGMT.ADD.FORM.VISIBILITY.PUBLIC.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ t('CANNED_MGMT.ADD.FORM.VISIBILITY.PUBLIC.DESCRIPTION') }}
              </p>
            </button>
            <button
              type="button"
              class="p-3 relative rounded-xl border text-left transition-all duration-200"
              :class="isVisibilityActive('private_response')"
              @click="onUpdateVisibility('private_response')"
            >
              <span
                v-if="visibility === 'private_response'"
                class="i-ph-check-circle-fill absolute text-[#4ade80] top-3 right-3 size-4"
              />
              <p class="text-sm font-semibold text-white m-0">
                {{ t('CANNED_MGMT.ADD.FORM.VISIBILITY.PRIVATE.LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ t('CANNED_MGMT.ADD.FORM.VISIBILITY.PRIVATE.DESCRIPTION') }}
              </p>
            </button>
          </div>
        </div>

        <template v-if="isPrivate">
          <div class="border-t border-white/10" />

          <div class="flex flex-col gap-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-bold text-[#4ade80] tracking-widest">{{
                formatStepNumber(2)
              }}</span>
              <span
                class="text-xs font-semibold tracking-[0.18em] text-n-slate-10 uppercase"
              >
                {{ t('CANNED_MGMT.ADD.FORM.SCOPE.LABEL') }}
              </span>
            </div>

            <div class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">
                {{ t('CANNED_MGMT.ADD.FORM.SCOPE.USER') }}
              </span>
              <TagMultiSelectComboBox
                v-model="selectedUserIds"
                class="max-w-xl rounded-xl border border-white/10 bg-white/5 transition-all duration-200 hover:border-[rgba(74,222,128,0.4)] focus-within:border-[rgba(74,222,128,0.5)]"
                :options="agentMultiSelectOptions"
                :placeholder="t('CANNED_MGMT.ADD.FORM.SCOPE.PLACEHOLDER')"
              />
            </div>

            <div class="flex flex-col gap-1">
              <span class="text-xs text-n-slate-11">
                {{ t('CANNED_MGMT.ADD.FORM.SCOPE.TEAM') }}
              </span>
              <TagMultiSelectComboBox
                v-model="selectedTeamIds"
                class="max-w-xl rounded-xl border border-white/10 bg-white/5 transition-all duration-200 hover:border-[rgba(74,222,128,0.4)] focus-within:border-[rgba(74,222,128,0.5)]"
                :options="teamMultiSelectOptions"
                :placeholder="t('CANNED_MGMT.ADD.FORM.SCOPE.PLACEHOLDER')"
              />
            </div>
          </div>
        </template>

        <div class="border-t border-white/10" />

        <div class="flex items-center justify-between gap-3 py-2">
          <Button
            faded
            slate
            type="reset"
            variant="link"
            class="h-10 hover:!no-underline hover:text-n-brand"
            :label="t('CANNED_MGMT.ADD.CANCEL_BUTTON_TEXT')"
            @click.prevent="props.onClose"
          />
          <Button
            type="submit"
            color="teal"
            :label="t('CANNED_MGMT.ADD.FORM.SUBMIT')"
            :disabled="isFormInvalid"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>
